/**
 * Brand Manager — IdeaWorks
 * Add and manage brand profiles with website scraping for AI context.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Globe, Trash2, RefreshCw, Check, AlertCircle, Building2, ExternalLink } from 'lucide-react';
import { FileAttachments } from '../components/FileAttachments';

const API = process.env.REACT_APP_BACKEND_URL;

const BRAND_COLORS = ['#7c5efa', '#00d4b8', '#f97316', '#3dffa0', '#60a5fa', '#f43f5e', '#a78bfa'];

function BrandCard({ brand, onDelete, onRescrape }) {
  const [scraping, setScraping] = useState(false);

  const rescrape = async () => {
    setScraping(true);
    try { await onRescrape(brand.id); } finally { setScraping(false); }
  };

  return (
    <div className="rounded-2xl p-5 flex flex-col gap-3 relative group" style={{ background: 'var(--llmc-surface)', border: `1px solid ${brand.color}33` }}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-black" style={{ background: `${brand.color}25`, color: brand.color }}>
            {brand.name[0].toUpperCase()}
          </div>
          <div>
            <div className="font-bold text-sm text-white">{brand.name}</div>
            {brand.tagline && <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>{brand.tagline}</div>}
          </div>
        </div>
        <button onClick={() => onDelete(brand.id)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg hover:text-red-400" style={{ color: 'var(--llmc-text-muted)' }}>
          <Trash2 size={14} />
        </button>
      </div>

      {brand.website_url && (
        <a href={brand.website_url.startsWith('http') ? brand.website_url : `https://${brand.website_url}`}
          target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-[11px] hover:opacity-80 transition-opacity"
          style={{ color: '#60a5fa' }}>
          <Globe size={10} /> {brand.website_url}
          <ExternalLink size={9} />
        </a>
      )}

      {brand.description && (
        <p className="text-[11px] leading-relaxed line-clamp-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
          {brand.description}
        </p>
      )}

      <div className="flex items-center gap-2 pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className={`flex items-center gap-1 text-[10px] ${brand.scraped_context ? 'text-green-400' : 'text-gray-500'}`}>
          {brand.scraped_context ? <Check size={10} /> : <AlertCircle size={10} />}
          {brand.scraped_context ? 'AI context ready' : 'No context scraped'}
        </div>
        {brand.website_url && (
          <button onClick={rescrape} disabled={scraping}
            className="ml-auto flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-lg transition-all hover:opacity-80 disabled:opacity-40"
            style={{ background: 'rgba(124,94,250,0.1)', color: '#a68eff' }}>
            <RefreshCw size={9} className={scraping ? 'animate-spin' : ''} />
            {scraping ? 'Scraping...' : 'Rescrape'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function BrandManager() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [form, setForm] = useState({
    name: '', website_url: '', tagline: '', description: '', color: BRAND_COLORS[0],
  });

  const loadBrands = async () => {
    try {
      const { data } = await axios.get(`${API}/api/brands`);
      setBrands(data.brands || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadBrands(); }, []);

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      let description = form.description;
      // Append attachment content to description/context
      if (attachments.length > 0) {
        const extra = attachments.map(a => `\n--- ${a.name} ---\n${a.content.slice(0, 2000)}`).join('\n');
        description = (description + extra).trim();
      }
      const { data } = await axios.post(`${API}/api/brands`, { ...form, description });
      setBrands(prev => [...prev, data.brand]);
      setForm({ name: '', website_url: '', tagline: '', description: '', color: BRAND_COLORS[0] });
      setAttachments([]);
      setShowForm(false);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save brand');
    } finally {
      setSaving(false);
    }
  };

  const deleteBrand = async (id) => {
    if (!window.confirm('Delete this brand?')) return;
    try {
      await axios.delete(`${API}/api/brands/${id}`);
      setBrands(prev => prev.filter(b => b.id !== id));
    } catch {}
  };

  const rescrape = async (id) => {
    try {
      const { data } = await axios.post(`${API}/api/brands/${id}/rescrape`);
      if (data.success) loadBrands();
    } catch {}
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Building2 size={20} style={{ color: '#00d4b8' }} />
            <h1 className="text-xl font-black text-white">Brand Manager</h1>
          </div>
          <p className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
            Add brands · AI scrapes website for context · Used across all content generation
          </p>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="add-brand-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all hover:scale-105"
          style={{ background: 'linear-gradient(135deg, #00d4b8, #3dffa0)', color: '#0a0a0f' }}>
          <Plus size={15} /> Add Brand
        </button>
      </div>

      {/* Add Brand Form */}
      {showForm && (
        <div className="rounded-2xl p-6 mb-6 space-y-4" style={{ background: 'var(--llmc-surface)', border: '1px solid rgba(0,212,184,0.3)' }}>
          <div className="text-sm font-bold text-white mb-2">New Brand</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Brand Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="llmc-input w-full px-3 py-2 text-sm" placeholder="e.g. QLIA.io" data-testid="brand-name-input" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Website URL</label>
              <div className="flex items-center gap-2">
                <Globe size={14} className="flex-shrink-0" style={{ color: 'var(--llmc-text-muted)' }} />
                <input value={form.website_url} onChange={e => setForm(f => ({ ...f, website_url: e.target.value }))}
                  className="llmc-input flex-1 px-3 py-2 text-sm" placeholder="qlia.io" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Tagline</label>
            <input value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))}
              className="llmc-input w-full px-3 py-2 text-sm" placeholder="One-line brand description" />
          </div>

          <div>
            <label className="block text-[10px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Additional Description / Context</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="llmc-input w-full px-3 py-2 text-sm resize-none" rows={3}
              placeholder="Products, services, target audience, tone of voice..." />
          </div>

          {/* Brand color + attachment */}
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <label className="block text-[10px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Brand Color</label>
              <div className="flex gap-2">
                {BRAND_COLORS.map(c => (
                  <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                    className="w-6 h-6 rounded-full transition-all hover:scale-110"
                    style={{ background: c, outline: form.color === c ? `2px solid white` : 'none', outlineOffset: 2 }} />
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--llmc-text-muted)' }}>Attach Docs</label>
              <FileAttachments attachments={attachments} onChange={setAttachments} />
            </div>
          </div>

          {form.website_url && (
            <div className="flex items-center gap-2 text-[11px] px-3 py-2 rounded-lg" style={{ background: 'rgba(0,212,184,0.07)', color: '#3dffa0' }}>
              <Globe size={12} />
              AI will automatically scrape {form.website_url} to extract brand context
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={!form.name.trim() || saving}
              data-testid="save-brand-btn"
              className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold transition-all hover:scale-105 disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #00d4b8, #3dffa0)', color: '#0a0a0f' }}>
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              {saving ? 'Saving & Scraping...' : 'Save Brand'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-xl text-sm transition-all hover:opacity-80"
              style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--llmc-text-muted)' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Brand list */}
      {loading ? (
        <div className="text-center py-20 text-sm" style={{ color: 'var(--llmc-text-muted)' }}>Loading brands...</div>
      ) : brands.length === 0 ? (
        <div className="rounded-2xl p-12 text-center" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
          <Building2 size={32} className="mx-auto mb-4 opacity-30" style={{ color: '#00d4b8' }} />
          <div className="text-sm font-semibold text-white mb-1">No brands yet</div>
          <div className="text-xs mb-4" style={{ color: 'var(--llmc-text-muted)' }}>Add your first brand to enable brand-aware content generation</div>
          <button onClick={() => setShowForm(true)} className="px-5 py-2 rounded-xl text-sm font-bold"
            style={{ background: 'rgba(0,212,184,0.15)', border: '1px solid rgba(0,212,184,0.4)', color: '#00d4b8' }}>
            Add Brand
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.map(b => (
            <BrandCard key={b.id} brand={b} onDelete={deleteBrand} onRescrape={rescrape} />
          ))}
        </div>
      )}
    </div>
  );
}
